import React, { useEffect } from "react";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { analytics } from "services/analytics";
import "components/Screens/Marketplace/Customer/common/VideoModal/VideoModal.scss";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  talentId?: string;
  talentName?: string;
}

function VideoModal({ isOpen, onClose, videoUrl, talentId, talentName }: VideoModalProps) {
  const trackProps = { talentId, talentName };

  const handlePlay = () => {
    analytics.track("Talent Video Playback Started", trackProps);
  };

  const handlePause = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (event.currentTarget.ended) return;
    analytics.track("Talent Video Paused", trackProps);
  };

  const handleSeeked = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const seekToSeconds = Math.floor(event.currentTarget.currentTime);
    const mm = Math.floor(seekToSeconds / 60)
      .toString()
      .padStart(2, "0");
    const ss = (seekToSeconds % 60).toString().padStart(2, "0");
    analytics.track("Talent Video Seeked", { ...trackProps, seekToSeconds, seekToMmSs: `${mm}:${ss}` });
  };

  const handleEnded = () => {
    analytics.track("Talent Video Completed", trackProps);
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="video-modal"
      aria-labelledby="video-modal-title"
      aria-describedby="video-modal-description"
    >
      <div className="video-modal__wrapper">
        <div className="video-modal__container">
          <div className="video-modal__content">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              className="video-modal__video"
              controls
              autoPlay
              playsInline
              preload="metadata"
              onPlay={handlePlay}
              onPause={handlePause}
              onSeeked={handleSeeked}
              onEnded={handleEnded}
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
        <button type="button" className="video-modal__close-btn" onClick={onClose} aria-label="Close video">
          <Close sx={{ fontSize: 20, color: "#8139D0" }} />
        </button>
      </div>
    </Modal>
  );
}

export default VideoModal;
